import React, { useEffect, useState } from "react";
import {
  RosterOwner,
  RosterPlayer,
  addRosterPlayer,
  deleteRosterPlayer,
  homeCourseLabel,
  loadOwner,
  loadRoster,
  saveOwner,
  updateRosterPlayer,
} from "./roster";
import PlayerEditModal, { EditablePerson } from "./PlayerEditModal";
import ConnectModal from "./modals/ConnectModal";
import ConfirmModal from "./modals/ConfirmModal";
import { DirectoryUser } from "./connectDirectory";
import { connectUser, shareRound } from "./connections";
import { loadHistory } from "./activeRound";
import { refreshConnections } from "./caddieGamesStore";
import RosterImport from "./RosterImport";
import { useAuth } from "../../hooks/useAuth";

interface Props {
  onBack: () => void;
}

// Edit mode markers: "owner" → owner card; "new" → add roster player;
// RosterPlayer → edit existing roster player; null → modal closed.
type Editing = "owner" | "new" | RosterPlayer | null;

// Roster screen. Owner (profile) card sits at the top (always visible —
// the user enters their own name/home course/HI once and it appears in
// every round's player picker). Below it, the saved-players list with
// add/edit/delete and per-row Connect.
export default function Players({ onBack }: Props) {
  const [owner, setOwner] = useState<RosterOwner | null>(null);
  const [roster, setRoster] = useState<RosterPlayer[]>([]);
  const [editing, setEditing] = useState<Editing>(null);
  // Roster player currently being linked via the Connect flow; null when
  // the search modal is closed.
  const [connecting, setConnecting] = useState<RosterPlayer | null>(null);
  // Roster player pending removal; drives the confirm dialog so the "×"
  // never deletes on a single accidental tap.
  const [removing, setRemoving] = useState<RosterPlayer | null>(null);
  // When true, the import subview replaces the roster list. Returns to
  // false on close; the roster list refreshes from localStorage on
  // re-mount via the refresh() call.
  const [importing, setImporting] = useState(false);
  // Account-deletion flow (profile only): confirm dialog + in-flight state.
  const [deletingAccount, setDeletingAccount] = useState(false);
  const [deleteBusy, setDeleteBusy] = useState(false);
  const [deleteError, setDeleteError] = useState("");
  const { logout } = useAuth();
  // Pull connection changes (new links + drops) on demand, without a logout —
  // so a captain sees a teammate connect / fall off right away.
  const [refreshing, setRefreshing] = useState(false);
  const handleRefresh = async () => {
    if (refreshing) return;
    setRefreshing(true);
    try {
      await refreshConnections();
      refresh();
    } finally {
      setRefreshing(false);
    }
  };

  const refresh = () => {
    setOwner(loadOwner());
    setRoster(loadRoster());
  };

  useEffect(() => {
    refresh();
  }, []);

  const handleOwnerSave = (data: EditablePerson) => {
    saveOwner(data);
    setEditing(null);
    refresh();
  };

  const handleRosterSave = (data: EditablePerson) => {
    if (editing && editing !== "new" && editing !== "owner") {
      updateRosterPlayer(editing.id, data);
    } else {
      addRosterPlayer(data);
    }
    setEditing(null);
    refresh();
  };

  const handleDelete = (id: string) => {
    deleteRosterPlayer(id);
    refresh();
  };

  // Link a roster entry to a real CaddieGames user. Three things happen:
  //   1. Local link (immediate UI) — merged onto the existing record, so no
  //      duplicate row is ever created.
  //   2. Backend bidirectional link — the other user gets me in their roster
  //      as Connected on their next hydrate (no approval / no action by them).
  //   3. Backfill — every past round this player appeared in is shared with
  //      the connected user so it shows in their history right away.
  const handleLink = async (user: DirectoryUser) => {
    if (!connecting) return;
    const player = connecting;
    updateRosterPlayer(player.id, {
      linkedUserId: user.userId,
      linkedName: user.name,
      linkedHomeCourse: user.homeCourse || undefined,
      // Pull the connected user's handicap index from their profile. Only
      // when they actually have one set, so connecting never clears an
      // existing handicap.
      ...(typeof user.handicapIndex === "number"
        ? { handicapIndex: user.handicapIndex }
        : {}),
    });
    setConnecting(null);
    refresh();

    await connectUser(user.userId);

    const targetName = player.name.trim().toLowerCase();
    const pastRounds = loadHistory().filter((r) =>
      r.players.some((p) => p.name.trim().toLowerCase() === targetName),
    );
    for (const round of pastRounds) {
      await shareRound(round, [user.userId]);
    }
  };

  // Open the delete-account confirm (close the profile editor first so the
  // confirm isn't stacked on top of it).
  const requestDeleteAccount = () => {
    setEditing(null);
    setDeleteError("");
    setDeletingAccount(true);
  };

  // Permanently delete the user's account + CaddieGames data, then sign out.
  // The backend decides full-delete vs CaddieGames-only revoke (shared
  // identity). On success we use the app's canonical logout so native token /
  // session flags are cleared and the user lands back on the login screen.
  const handleConfirmDeleteAccount = async () => {
    if (deleteBusy) return;
    setDeleteBusy(true);
    setDeleteError("");
    try {
      const res = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/caddiegames/account`,
        { method: "DELETE", credentials: "include" },
      );
      if (!res.ok) throw new Error();
      await logout();
    } catch {
      setDeleteError("Couldn't delete your account. Please try again.");
      setDeleteBusy(false);
      setDeletingAccount(false);
    }
  };

  const editProps = (() => {
    if (editing === "owner") {
      return {
        title: owner ? "Edit profile" : "Set up profile",
        initial: owner || undefined,
        showCurrency: true,
        showHomeCourse: true,
        connectedHomeCourse: undefined as string | undefined,
        // Account deletion lives on the profile, and must be reachable for any
        // logged-in user (Apple 5.1.1(v)) — including before they've set one up.
        onDeleteAccount: requestDeleteAccount,
        onSave: handleOwnerSave,
      };
    }
    if (editing === "new") {
      return {
        title: "Add player",
        initial: undefined,
        showCurrency: false,
        showHomeCourse: false,
        connectedHomeCourse: undefined as string | undefined,
        onDeleteAccount: undefined as (() => void) | undefined,
        onSave: handleRosterSave,
      };
    }
    if (editing) {
      return {
        title: "Edit player",
        initial: editing,
        showCurrency: false,
        showHomeCourse: false,
        // Read-only — surfaced in the details view to disambiguate connected
        // users with the same name (kept out of the roster list itself). A
        // connected player with no home course set reads as "Public Player".
        connectedHomeCourse: editing.linkedUserId
          ? homeCourseLabel(editing.linkedHomeCourse)
          : undefined,
        onDeleteAccount: undefined as (() => void) | undefined,
        onSave: handleRosterSave,
      };
    }
    return null;
  })();

  if (importing) {
    return (
      <RosterImport
        onApplied={refresh}
        onClose={() => setImporting(false)}
      />
    );
  }

  return (
    <div className="cg-subpage cg-roster-subpage">
      <div className="cg-subpage-head">
        <button className="cg-btn cg-btn-ghost" onClick={onBack}>
          ← Back
        </button>
        <h2>Players</h2>
        <button
          type="button"
          className={`cg-roster-refresh${refreshing ? " spinning" : ""}`}
          onClick={handleRefresh}
          disabled={refreshing}
          aria-label="Refresh connections"
          title="Refresh"
        >
          ↻
        </button>
      </div>

      {deleteError && <div className="cg-roster-error">{deleteError}</div>}

      {/* Owner card — always at the top. Empty state when not set up. */}
      <div className="cg-owner-section">
        <div className="cg-owner-label">You</div>
        {owner ? (
          <button
            type="button"
            className="cg-owner-row"
            onClick={() => setEditing("owner")}
          >
            <div className="cg-roster-info">
              <span className="cg-roster-name">{owner.name}</span>
            </div>
            {owner.handicapIndex != null && (
              <span className="cg-roster-hcp">HI {owner.handicapIndex}</span>
            )}
          </button>
        ) : (
          <button
            type="button"
            className="cg-owner-empty"
            onClick={() => setEditing("owner")}
          >
            + Set up your profile
          </button>
        )}
      </div>

      <div className="cg-roster-section">
        <div className="cg-roster-section-head">
          <h3>Roster</h3>
          <div className="cg-roster-actions">
            <button
              type="button"
              className="cg-btn cg-btn-ghost cg-roster-import"
              onClick={() => setImporting(true)}
            >
              Import from Screenshot
            </button>
            <button
              type="button"
              className="cg-btn cg-btn-primary cg-roster-add"
              onClick={() => setEditing("new")}
            >
              + Add
            </button>
          </div>
        </div>

        {roster.length === 0 ? (
          <p className="cg-subpage-note">No players in roster yet.</p>
        ) : (
          <ul className="cg-roster-list">
            {roster.map((p) => (
              <li key={p.id} className="cg-roster-row">
                <button
                  type="button"
                  className="cg-roster-main"
                  onClick={() => setEditing(p)}
                >
                  <div className="cg-roster-info">
                    <span className="cg-roster-name">{p.name}</span>
                  </div>
                  {p.handicapIndex != null && (
                    <span className="cg-roster-hcp">HI {p.handicapIndex}</span>
                  )}
                </button>
                {p.linkedUserId ? (
                  <span className="cg-roster-connected" title="Connected">
                    ✔ Connected
                  </span>
                ) : (
                  <button
                    type="button"
                    className="cg-roster-connect"
                    onClick={() => setConnecting(p)}
                  >
                    Connect
                  </button>
                )}
                <button
                  type="button"
                  className="cg-roster-remove"
                  onClick={(e) => {
                    e.stopPropagation();
                    setRemoving(p);
                  }}
                  aria-label={`Remove ${p.name}`}
                  title="Remove"
                >
                  ×
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>

      {editProps && (
        <PlayerEditModal
          initial={editProps.initial}
          title={editProps.title}
          showCurrency={editProps.showCurrency}
          showHomeCourse={editProps.showHomeCourse}
          connectedHomeCourse={editProps.connectedHomeCourse}
          onDeleteAccount={editProps.onDeleteAccount}
          onSave={editProps.onSave}
          onClose={() => setEditing(null)}
        />
      )}

      {deletingAccount && (
        <ConfirmModal
          title="Delete account?"
          message="This permanently deletes your CaddieGames account, profile, roster, and round history. This can't be undone."
          confirmLabel={deleteBusy ? "Deleting…" : "Delete account"}
          variant="danger"
          onConfirm={handleConfirmDeleteAccount}
          onCancel={() => {
            if (deleteBusy) return;
            setDeletingAccount(false);
          }}
        />
      )}

      {connecting && (
        <ConnectModal
          initialQuery={connecting.name}
          onLink={handleLink}
          onClose={() => setConnecting(null)}
        />
      )}

      {removing && (
        <ConfirmModal
          title="Remove player"
          message={`Are you sure you want to remove ${removing.name} from your Roster?`}
          confirmLabel="Remove"
          variant="danger"
          onConfirm={() => {
            handleDelete(removing.id);
            setRemoving(null);
          }}
          onCancel={() => setRemoving(null)}
        />
      )}
    </div>
  );
}
